import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FARES_FAMILY, StatusBag, TypeBagsWeight } from '@app/enums';
import { Passenger as PassengerOld } from '@app/models';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro';
import { BagAncillarieInt, BagPassangerInt } from '@app/interfaces';
import { Subscription } from 'rxjs';
import { on } from 'events';

/**
 * Componente para mostrar el estado de una maleta.
 *
 */
@Component({
  selector: 'am-bag-cell-carryOn',
  templateUrl: './am-bag-cell-carryOn.component.html',
  styleUrls: ['./am-bag-cell-carryOn.component.scss'],
  standalone: false,
})
export class AmBagCellCarryOnComponent implements OnInit {
  // #region Vars input
  /**
   * Lista de maletas adicionales disponibles para los pasajeros.
   *
   * @type {BagAncillarieInt[]}
   */
  @Input() extraBags: BagAncillarieInt[];

  /**
   * Tipo de tarifa del pasajero.
   *
   * @type {string}
   */
  @Input() fareFamily: string;

  /**
   * Información de la reserva actual.
   *
   * @type {Reservation}
   */
  @Input() reservation: Reservation;

  /**
   * Indica si el pago por maleta está habilitado.
   *
   * @type {boolean}
   * @default true
   */
  @Input() hasBagPayment: boolean = true;

  /**
   * Indica si hay maletas de 20kg disponibles.
   *
   * @type {boolean}
   * @default false
   */
  @Input() hasBags20Kg: boolean = false;

  @Input() extraBagsCarryon: BagAncillarieInt[];

  /**
   * Indica si hay maletas de 25kg disponibles.
   *
   * @type {boolean}
   * @default false
   */
  @Input() hasBags25Kg: boolean = false;

  /**
   * Indica si hay maletas de 32kg disponibles.
   *
   * @type {boolean}
   * @default false
   */
  @Input() hasBags32Kg: boolean = false;

  /**
   * Indica si se puede canjear equipaje.
   *
   * @type {boolean}
   * @default false
   */
  @Input() hasRedeeme: boolean = false;

  /**
   * Lista de pasajeros en la reserva.
   *
   * @type {Passenger[]}
   */
  @Input() passengers: Passenger[];
  @Input() passengersCarryOn: Passenger[];
  @Input() enableCheck: boolean = false;
  /**
   * Indica si el pasajero tiene tarifa Premier.
   *
   * @type {boolean}
   * @default false
   */
  @Input() isPremier: boolean = false;

  /**
   * Controla la vista del modo Premier.
   * Al cambiar su valor, se actualiza el estado global del equipaje.
   *
   * @param value Indica si la vista Premier está activa.
   */

  @Input() set setViewPremier(value: boolean) {
    this.isViewPremier = value;
    if (this.isViewPremier && this.passengers) {
      this.getStatusGlobalBags();
    }
  }

  /**
   * Evento emitido cuando se actualiza el estado del equipaje.
   */
  @Output() updateBags: EventEmitter<any> = new EventEmitter();

  /**
   * Evento emitido cuando se canjea un equipaje.
   */
  @Output() redeem: EventEmitter<any> = new EventEmitter();
  // #endregion Vars input

  @Input() passenger: Passenger;
  @Input() passengerCarryOn: Passenger;
  @Input() typeBagsWeight: TypeBagsWeight = TypeBagsWeight._10KG;
  @Input() enablePlus: boolean = true;
  @Input() enableClassic: boolean = false;
  @Input() statusBag: StatusBag;
  @Input() has5kg = false;
  @Input() isDomestic: boolean;
  @Input() count: number = 0;
  @Input() isConnectionBlocked = false;

  isViewPremier: boolean = false;
  @Output() plus: EventEmitter<any> = new EventEmitter<any>();
  @Output() minus: EventEmitter<any> = new EventEmitter<any>();
  @Output() onChangeExtraWeight: EventEmitter<boolean> = new EventEmitter<boolean>();
  StatusBag = StatusBag;
  bags20KGPaid: BagPassangerInt[];
  bags25KGPaid: BagPassangerInt[];
  bags32KGPaid: BagPassangerInt[];
  BagsWeight = TypeBagsWeight;
  currencyCodeBags: string;
  statusGlobalBags: StatusBag;
  totalAmountBags: number = 0;
  subs: Array<Subscription> = [];
  @Output() redeemRequest: EventEmitter<{ passenger: Passenger; target?: 'checked' | 'carryon' }> =
    new EventEmitter();
  @Output() bagsChanged = new EventEmitter<void>();

  limitBags: number = 0;
  readonly maxCarryOnPerPassenger: number = 1;

  constructor() {}

  ngOnInit(): void {
    this.limitBags = this.passenger?.bagOfferStack?.length || 0;
  }

  get title() {
    const titles = {
      [TypeBagsWeight._10KG]: 'bagSelectionCarryonBasic.itemBag.titles.title-10kg',
      [TypeBagsWeight._15KG]: 'bagSelectionCarryonBasic.itemBag.titles.title-15kg',
      [TypeBagsWeight._20KG]: 'bagSelectionCarryonBasic.itemBag.titles.title-20kg',
      [TypeBagsWeight._25KG]: 'bagSelectionCarryonBasic.itemBag.titles.title-25kg',
      [TypeBagsWeight._32KG]: 'bagSelectionCarryonBasic.itemBag.titles.title-32kg',
    };

    // Para MAIN_LIGHT: si typeBagsWeight es undefined/null y hay hasBags25Kg, mostrar "Documentado"
    // Esto aplica a la segunda columna (equipaje documentado)
    if (
      !this.typeBagsWeight &&
      (this.fareFamily === 'MAIN_LIGHT' || this.fareFamily === 'main_light') &&
      (this.hasBags25Kg || this.hasBags20Kg || this.hasBags32Kg)
    ) {
      return 'bagSelectionCarryonBasic.itemBag.titles.title-25kg'; // "Documentado"
    }

    return titles[this.typeBagsWeight] || titles[TypeBagsWeight._10KG];
  }

  get subtitle() {
    const subtitles = {
      [TypeBagsWeight._10KG]: 'bagSelectionCarryonBasic.itemBag.subtitles.subtitle-10kg',
      [TypeBagsWeight._15KG]: 'bagSelectionCarryonBasic.itemBag.subtitles.subtitle-15kg',
      [TypeBagsWeight._20KG]: 'bagSelectionCarryonBasic.itemBag.subtitles.subtitle-20kg',
      [TypeBagsWeight._25KG]: 'bagSelectionCarryonBasic.itemBag.subtitles.subtitle-25kg',
      [TypeBagsWeight._32KG]: 'bagSelectionCarryonBasic.itemBag.subtitles.subtitle-32kg',
    };

    if (
      !this.typeBagsWeight &&
      (this.fareFamily === 'MAIN_LIGHT' || this.fareFamily === 'main_light') &&
      (this.hasBags25Kg || this.hasBags20Kg || this.hasBags32Kg)
    ) {
      return 'bagSelectionCarryonBasic.itemBag.subtitles.subtitle-25kg'; // "25kg | 158 cm lineales"
    }

    return this.hasbagToBenefit
      ? 'bagSelectionCarryonBasic.itemBag.bagBenefit'
      : subtitles[this.typeBagsWeight] || subtitles[TypeBagsWeight._10KG];
  }

  get hasBagToInclude() {
    if (this.typeBagsWeight === TypeBagsWeight._10KG) {
      if (this.fareFamily === 'MAIN_LIGHT' || this.fareFamily === 'main_light') {
        const hasPaidInBagStackCarryon = this.passengerCarryOn.bagStackCarryon?.some(
          (bag) => bag.status === StatusBag.PAID && !bag.used
        );
        return hasPaidInBagStackCarryon;
      }

      return this.passengerCarryOn.bagStackCarryon?.some(
        (bag) => bag.status === StatusBag.INCLUDE && !bag.used
      );
    } else {
      return this.passengerCarryOn.bagStack?.some(
        (bag) => bag.status === StatusBag.INCLUDE && !bag.used
      );
    }
  }

  get hasbagToBenefit() {
    if (this.enableCheck) return false;

    // CI-19584: columna Carry-On (10KG) puede tener beneficio INBURSA
    // (REDEEMED/REDEEMED_APPLIED en bagStackCarryon). Antes el getter solo
    // miraba bagStack (checked) y nunca mostraba el chip para Carry-On.
    if (this.typeBagsWeight === TypeBagsWeight._10KG) {
      const carryon = this.passengerCarryOn?.bagStackCarryon?.some(
        (bag) =>
          bag.status === StatusBag.REDEEMED ||
          (bag.status === StatusBag.REDEEMED_APPLIED && !bag.used)
      );
      return !!carryon;
    }

    const value = this.passengerCarryOn?.bagStack?.some(
      (bag) =>
        bag.status === StatusBag.REDEEMED ||
        (bag.status === StatusBag.REDEEMED_APPLIED && !bag.used)
    );
    return !!value;
  }

  /**
   * CI-19584 — true cuando hay una redención Carry-On (INBURSA) disponible
   * (pendiente) para esta celda. Gobierna el switch "Usar beneficio".
   */
  get hasCarryonRedeemAvailable(): boolean {
    if (this.typeBagsWeight !== TypeBagsWeight._10KG) return false;
    return !!this.passengerCarryOn?.bagStackCarryon?.some(
      (bag) => bag.status === StatusBag.REDEEMED && !bag.used
    );
  }

  /**
   * CI-19584 — true cuando el Carry-On INBURSA ya fue redimido/aplicado.
   * Gobierna el banner "Equipaje sin costo ... aplicado." + badge "Redimida".
   */
  get hasCarryonApplied(): boolean {
    if (this.typeBagsWeight !== TypeBagsWeight._10KG) return false;
    return !!this.passengerCarryOn?.bagStackCarryon?.some(
      (bag) => bag.status === StatusBag.REDEEMED_APPLIED && !bag.used
    );
  }

  /**
   * Determina si debe mostrar el counter para comprar maleta (solo para MAIN_LIGHT en carry-on).
   * Se muestra cuando: fareFamily es MAIN_LIGHT, es tipo 10KG, y la maleta NO está pagada.
   */
  get showCounterForMainLight() {
    if (
      this.typeBagsWeight === TypeBagsWeight._10KG &&
      (this.fareFamily === 'MAIN_LIGHT' ||
        this.fareFamily === 'main_light' ||
        this.fareFamily === 'BASIC' ||
        this.fareFamily === 'basic' ||
        this.fareFamily === 'MAIN_BASIC' ||
        this.fareFamily === 'main_basic')
    ) {
      // Mostrar counter solo si NO hay maletas pagadas
      const hasPaidBag = this.passengerCarryOn.bagStackCarryon?.some(
        (bag) => bag.status === StatusBag.PAID && !bag.used
      );
      return !hasPaidBag;
    }
    return false;
  }

  get showCounterForMainLightWithoutPaid() {
    const isCarryOnFareFamily =
      this.fareFamily === 'MAIN_LIGHT' ||
      this.fareFamily === 'main_light' ||
      this.fareFamily === 'BASIC' ||
      this.fareFamily === 'basic' ||
      this.fareFamily === 'MAIN_BASIC' ||
      this.fareFamily === 'main_basic';

    if (this.showCounterForMainLight && isCarryOnFareFamily) {
      return !this.hasBagToInclude; // No mostrar si ya está pagada
    }
    return this.showCounterForMainLight; // Para otros casos, usar lógica original
  }

  get showCarryOnWeightInfo() {
    // No mostrar en carryon-classic
    if (this.enableClassic || this.enableCheck) {
      return false;
    }

    // No mostrar para USA
    if (this.reservation?.flightSummary?.regionKey === 'USA') {
      return false;
    }

    // Solo para columna carry-on (10kg)
    if (this.typeBagsWeight !== TypeBagsWeight._10KG) {
      return false;
    }

    // Solo para tarifa BASIC
    if (
      this.fareFamily !== FARES_FAMILY.MAIN_BASIC &&
      this.fareFamily !== FARES_FAMILY.AMPLUS_BASIC &&
      this.fareFamily !== FARES_FAMILY.PREMIER_BASIC
    ) {
      return false;
    }

    // Verificar si hay bag PAID en bagStackCarryon O en bagOfferCarryOnStack
    const hasPaidInBagStack =
      this.passengerCarryOn.bagStackCarryon?.some((bag) => bag.status === StatusBag.PAID) || false;

    const hasPaidInBagOffer =
      this.passengerCarryOn.bagOfferCarryOnStack?.some((bag) => bag.status === StatusBag.PAID) ||
      false;

    return hasPaidInBagStack || hasPaidInBagOffer;
  }

  get isConnectionWithUsedFirstLeg(): boolean {
    const segs = this.reservation?.flightSummary?.segments;
    if ((segs?.length ?? 0) <= 1) {
      return false;
    }

    const firstSegStatus = segs[0]?.windowSegmentStatus;
    if (firstSegStatus === 'CLOSED' || firstSegStatus === 'IN_FLIGHT' || firstSegStatus === 'MYB') {
      return true;
    }

    const firstSegCode = segs[0]?.segmentCode;
    return (
      this.passengerCarryOn?.coupons?.some(
        (c) => c.segmentCode === firstSegCode && c.couponStatus === 'USED'
      ) ?? false
    );
  }

  //#region Events
  onPlus() {
    if (this.typeBagsWeight != TypeBagsWeight._10KG && this.count < this.limitBags) {
      this.count++;
      this.plus.emit(this.count);
      this.bagsChanged.emit();
    } else {
      if (this.count < 1) {
        this.count++;
        this.plus.emit(this.count);
        this.bagsChanged.emit();
      }
    }
  }

  onMinus() {
    if (this.count > 0) {
      this.count--;
      this.minus.emit(this.count);
      this.bagsChanged.emit();
    }
  }

  onPlusBlocked() {
    if (this.isConnectionBlocked) {
      this.plus.emit(-1);
    }
  }

  onMinusBagConnection(event) {
    this.passengerCarryOn.onMinusBagsType(event, [TypeBagsWeight._25KG, TypeBagsWeight._32KG]);
  }

  /** Maneja el + bloqueado por conexión desde am-counter */
  onBlockedPlusBag(countBags: number) {
    const allowedStatus = [StatusBag.INCLUDE, StatusBag.PAID, StatusBag.REDEEMED_APPLIED];
    const deselectedBag = this.passengerCarryOn.bagStack.find(
      (x) =>
        (x.typeBagWeight === TypeBagsWeight._25KG || x.typeBagWeight === TypeBagsWeight._32KG) &&
        !x.used &&
        !x.selected &&
        allowedStatus.includes(x.status)
    );

    if (deselectedBag) {
      deselectedBag.selected = true;
      this.syncCounter++;
      this.getStatusGlobalBags();
      this.bagsChanged.emit();
    } else {
      this.plus.emit(-1); // Señal al padre para mostrar modal
    }
  }

  /** Contador para forzar sincronización del am-counter */
  syncCounter = 0;

  get subtitleClassic() {
    return '32 x 30 in | + $300 MXN';
  }

  get subtitleCostClassic() {
    return '60 x 48 in ';
  }

  /**
   * Calcula el estado global del equipaje basado en los pasajeros.
   */
  getStatusGlobalBags() {
    const statusPassangers = this.passengersCarryOn?.map((x) => {
      return this.getStatusBagColumnPassenger(x);
    });
    const bagsReeemedAplied = statusPassangers?.find((x) => x == StatusBag.REDEEMED_APPLIED);
    const bagsInclude = statusPassangers?.find((x) => x == StatusBag.INCLUDE);
    const bagsPaid = statusPassangers?.find((x) => x == StatusBag.PAID);
    const bagsToPay = statusPassangers?.find((x) => x == StatusBag.TO_PAY);

    if (bagsToPay) {
      if (!this.isPremier || (this.isPremier && this.isViewPremier)) {
        this.statusGlobalBags = StatusBag.TO_PAY;
      }
    } else if (bagsPaid) {
      this.statusGlobalBags = StatusBag.PAID;
    } else if (bagsReeemedAplied) {
      if (!this.isPremier || (this.isPremier && this.isViewPremier)) {
        this.statusGlobalBags = StatusBag.REDEEMED_APPLIED;
      }
    } else if (bagsInclude) this.statusGlobalBags = StatusBag.INCLUDE;
    else this.statusGlobalBags = StatusBag.NONE;
    this.totalAmountBags = 0;
    if (this.statusGlobalBags === StatusBag.TO_PAY) {
      this.passengersCarryOn.forEach((pax) => {
        const bags = pax.bagStack.filter((bag) => bag.status === StatusBag.TO_PAY);
        bags.forEach((bag) => {
          this.totalAmountBags += bag.currency?.total || 0;
          this.currencyCodeBags = bag.currency?.currencyCode || '';
        });
      });
    }

    this.updateBags.emit({
      totalAmountBags: this.totalAmountBags,
      currencyCodeBags: this.currencyCodeBags,
    });
  }

  getStatusBagColumnPassenger(passenger: Passenger) {
    if (this.isPremier) {
      return passenger.getStatusBagColumnPremier(this.isViewPremier);
    } else {
      return passenger.getStatusBagColumn();
    }
  }

  onHandleCheck(value: boolean) {
    this.onChangeExtraWeight.next(value);
  }

  /**
   * Obtiene la cantidad total de maletas seleccionadas por tipo de peso.
   *
   * @param bagWeight Peso del equipaje.
   * @returns Número total de maletas seleccionadas.
   */

  totalBagsForWeight(bagWeight: TypeBagsWeight) {
    let total = 0;

    this.passengers.map((passenger) => {
      total +=
        this.passengerCarryOn.bagStack.filter(
          (bag) => bag.typeBagWeight == bagWeight && bag.selected
        )?.length || 0;
    });

    return total;
  }

  // getBagsPrePaid() {
  //   this.bags20KGPaid = [];
  //   this.bags25KGPaid = [];
  //   this.bags32KGPaid = [];

  //   for (const passenger of this.passengers) {
  //     this.bags20KGPaid.push(...this.passengerOld.bagsPrePaid(TypeBagsWeight._20KG));
  //     this.bags25KGPaid.push(...this.passengerOld.bagsPrePaid(TypeBagsWeight._25KG));
  //     this.bags32KGPaid.push(...this.passengerOld.bagsNotUsed(TypeBagsWeight._32KG));
  //   };

  //   this.bags20KGPaid.map(bag => bag.selected = true);
  //   this.bags25KGPaid.map((bag) => bag.selected = true);
  //   this.bags32KGPaid.map((bag) => bag.selected = true);
  // }

  get isReviewBags() {
    return (
      !this.isViewPremier &&
      (this.bags20KGPaid?.length > 0 || this.bags25KGPaid?.length > 0) &&
      this.isPremier
    );
  }

  // #endregion Functions internals

  // #region Events
  onRedeemed(event, passenger) {
    this.redeem.emit({ event, passenger });
  }
  handleCountBags($event) {
    this.getStatusGlobalBags();
    this.bagsChanged.emit();
  }

  onPlusBag(event, extraBags, isDomestic) {
    if (this.passengerCarryOn.isAllowedToRedeem) {
      this.redeemRequest.emit({ passenger: this.passengerCarryOn, target: 'checked' });
      this.getStatusGlobalBags();
      this.bagsChanged.emit();
      return;
    }
    this.passengerCarryOn.onPlus(event, isDomestic);
    this.getStatusGlobalBags();
    this.bagsChanged.emit();
  }

  /**
   * CI-19584 — Handler explícito para canje Carry-On (INBURSA).
   * Lo dispara la celda cuando el chip "Canjear" se activa sobre la columna
   * 10KG y hay un Carry-On REDEEMED disponible en `bagStackCarryon`.
   */
  onRedeemCarryon(): void {
    if (!this.hasCarryonRedeemAvailable) return;
    this.redeemRequest.emit({ passenger: this.passengerCarryOn, target: 'carryon' });
  }

  /**
   * CI-19584 — Estado local del switch "Usar beneficio" (Carry-On INBURSA).
   * Permanece encendido mientras el modal está abierto; la página lo restaura
   * a apagado de forma determinista (resetBenefitSwitch) si el modal termina
   * sin éxito (cancelación / dismiss / NO_REDEEM / error).
   */
  carryonBenefitSwitch = false;

  /**
   * CI-19584 — Handler del switch. Al activarlo dispara la apertura del modal
   * (vía redeemRequest target='carryon'). NO se resetea aquí: el resultado del
   * modal decide si se restaura (cancelación/error) o se conserva (éxito,
   * donde el switch se oculta por estado adquirido).
   */
  onToggleCarryonBenefit(value: boolean): void {
    if (value) {
      this.onRedeemCarryon();
    }
  }

  /**
   * CI-19584 — Restaura el switch a apagado. Lo invoca la página al resolverse
   * el modal sin éxito (cancelación / dismiss / NO_REDEEM / error), de forma
   * determinista (sin manipulación de DOM ni setTimeout).
   */
  resetBenefitSwitch(): void {
    this.carryonBenefitSwitch = false;
  }

  onCount() {
    let count = 0;
    // Para main_light con carry-on (10kg)
    if (this.showCounterForMainLight) {
      // Ahora siempre usar bagStackCarryon (donde se agregan las maletas seleccionadas)
      count =
        this.passengerCarryOn.bagStackCarryon?.filter(
          (bag) =>
            (bag.status === StatusBag.TO_PAY ||
              bag.status === StatusBag.PAID ||
              bag.status === StatusBag.REDEEMED) &&
            !bag.used
        ).length || 0;
      return count;
    }

    switch (true) {
      case this.hasBags25Kg == true:
        count = this.passengerCarryOn.selectedBags25KgAnd32kg;
        break;
      case this.hasBags32Kg == true:
        count = this.passengerCarryOn.bags32Kg;
        break;
    }
    return count;
  }

  /**
   * CI-19510: Calcula cuántas maletas son removibles (solo TO_PAY)
   * Se usa para deshabilitar el botón "-" cuando solo quedan maletas incluidas
   */
  get removableBagsCount(): number {
    if (this.typeBagsWeight === TypeBagsWeight._10KG) return 0;
    return (
      this.passengerCarryOn.bagStack?.filter(
        (bag) =>
          (bag.typeBagWeight === TypeBagsWeight._25KG ||
            bag.typeBagWeight === TypeBagsWeight._32KG) &&
          !bag.used &&
          bag.selected
      ).length || 0
    );
  }

  /**
   * Maneja el incremento de carry-on para main_light
   */
  onPlusCarryOnMainLight() {
    const currentCount = this.onCount();
    if (currentCount >= this.maxCarryOnPerPassenger) {
      return;
    }

    const availableBag = this.passengerCarryOn.bagOfferCarryOnStack?.find(
      (bag) => bag.status === StatusBag.NONE || (bag.status === StatusBag.INCLUDE && !bag.used)
    );

    if (!availableBag) {
      return;
    }

    const added = this.passengerCarryOn.addCarryOnBagToPay(
      TypeBagsWeight._10KG,
      availableBag,
      this.maxCarryOnPerPassenger
    );

    if (added) {
      this.passengerCarryOn.isSelectForBagagge = true;
      this.getStatusGlobalBags();
    }
  }

  /**
   * Maneja el decremento de carry-on para main_light
   */
  onMinusCarryOnMainLight() {
    // Buscar maleta TO_PAY en bagStackCarryon para remover
    const bagToPay = this.passengerCarryOn.bagStackCarryon?.find(
      (bag) => bag.status === StatusBag.TO_PAY && !bag.used
    );

    if (bagToPay) {
      // Remover la maleta de bagStackCarryon (como onMinusBag)
      const index = this.passengerCarryOn.bagStackCarryon.indexOf(bagToPay);
      if (index > -1) {
        this.passengerCarryOn.bagStackCarryon.splice(index, 1);
      }
      this.getStatusGlobalBags();
    }
  }

  get cost() {
    let totalToPay = 0;
    this.passengerCarryOn.bagStack.forEach((bag) => {
      if (bag.status === StatusBag.TO_PAY) {
        totalToPay += bag.currency?.total || 0;
      }
    });

    return totalToPay;
  }

  /**
   * Maneja el cambio de peso adicional de maletas.
   * Se usa para carryon-classic con enableCheck=true
   */
  handleChangeExtraWeightBags($event, passenger: Passenger) {
    // Bloquear en conexión
    if (this.isConnectionBlocked) {
      this.plus.emit(-1);
      return;
    }
    passenger.onPlusExtraWeightCarryon($event, this.extraBagsCarryon, passenger);
    this.getStatusGlobalBags();
    this.bagsChanged.emit();
  }

  // #endregion Events

  //#endregion Events
}
